import React, { useState, useEffect } from 'react';
import { useTalentos } from '../../../../application/talentos/hooks/useTalentos';
import { useCreditCodes } from '../../../../application/talentos/hooks/useCreditCodes';
import ToolbarComponent from '../../../ui/section';
import { PrimaryButton } from '../../../ui/button';
import Input from '../../../ui/input';
import { Row, Col } from '../../../ui/grid';
import NavigationService from '../../../../utils/history';
import './style.css';

const GenerateCreditCodePage = () => {
    const talentosHook = useTalentos();
    const creditCodesHook = useCreditCodes();

    const {
        generateCreditCode,
        getEmployers
    } = talentosHook;

    // Si no está disponible, usar directamente del hook de creditCodes
    const finalGenerateCreditCode = generateCreditCode || creditCodesHook.generateCode;

    // Usar la función del hook actualizada
    const checkCreditEligibility = creditCodesHook.checkEligibilityHook || creditCodesHook.checkEligibility;

    const [employers, setEmployers] = useState([]);
    const [selectedEmployer, setSelectedEmployer] = useState(null);
    const [employerSearch, setEmployerSearch] = useState('');
    const [showEmployerDropdown, setShowEmployerDropdown] = useState(false);
    const [filteredEmployers, setFilteredEmployers] = useState([]);
    const [credits, setCredits] = useState(10);
    const [description, setDescription] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [eligibility, setEligibility] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        loadEmployers();
    }, []);

    useEffect(() => {
        if (selectedEmployer) {
            checkEmployerEligibility();
        }
    }, [selectedEmployer]);

    const loadEmployers = async () => {
        setIsLoading(true);
        try {
            const data = await getEmployers({ limit: 100 });
            const employersList = Array.isArray(data) ? data : (data?.employers || []);
            setEmployers(employersList);
        } catch (error) {
            console.error('Error loading employers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Función para filtrar empleadores por búsqueda
    const filterEmployers = (searchTerm) => {
        if (!searchTerm.trim()) {
            setFilteredEmployers([]);
            return;
        }

        const filtered = employers.filter(employer =>
            employer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employer.company?.toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, 10); // Limitar a 10 resultados

        setFilteredEmployers(filtered);
    };

    // Manejar cambios en la búsqueda de empleadores
    const handleEmployerSearch = (value) => {
        setEmployerSearch(value);
        filterEmployers(value);
        setShowEmployerDropdown(value.length > 0);
        // Limpiar selección anterior si cambia la búsqueda
        if (selectedEmployer && value !== selectedEmployer.name && value !== selectedEmployer.email) {
            setSelectedEmployer(null);
            setEligibility(null);
        }
    };

    // Seleccionar un empleador del dropdown
    const selectEmployer = (employer) => {
        setSelectedEmployer(employer);
        setEmployerSearch(`${employer.name} - ${employer.email}`);
        setShowEmployerDropdown(false);
        setEligibility(null);
        // Verificar elegibilidad del empleador seleccionado
        checkEmployerEligibility(employer._id);
    };

    // Limpiar selección de empleador
    const clearEmployerSelection = () => {
        setSelectedEmployer(null);
        setEmployerSearch('');
        setEligibility(null);
        setShowEmployerDropdown(false);
    };

    const checkEmployerEligibility = async (employerId) => {
        try {
            const eligibilityData = await checkCreditEligibility(employerId || selectedEmployer._id);

            // Si la API dice que no es elegible por "nunca haber recibido códigos",
            // considerarlo elegible para nuevos empleadores (lógica de negocio válida)
            if (!eligibilityData.eligible && (
                eligibilityData.reason?.includes('nunca') ||
                eligibilityData.reason?.includes('Nunca') ||
                eligibilityData.reason?.includes('no ha') ||
                eligibilityData.reason?.includes('no recibido'))) {
                // Empleador nuevo detectado - permitiendo generación automática
                setEligibility({
                    eligible: true,
                    reason: 'Empleador nuevo - elegible para primer código',
                    isNewEmployer: true
                });
            } else {
                setEligibility(eligibilityData);
            }
        } catch (error) {
            console.error('Error verificando elegibilidad:', error);

            // Diagnosticar el tipo de error
            let errorReason = 'Error al verificar elegibilidad - contacte al administrador';
            let hasConnectionError = false;

            if (error.code === 'ECONNREFUSED') {
                errorReason = 'No se puede conectar al servidor de elegibilidad';
                hasConnectionError = true;
            } else if (error.code === 'ENOTFOUND') {
                errorReason = 'Servidor de elegibilidad no encontrado';
                hasConnectionError = true;
            } else if (error.response) {
                errorReason = `Error del servidor: ${error.response.status} - ${error.response.statusText}`;
                if (error.response.data?.error) {
                    errorReason += ` (${error.response.data.error})`;
                }
            } else if (error.request) {
                errorReason = 'No se recibió respuesta del servidor de elegibilidad';
                hasConnectionError = true;
            }

            setEligibility({
                eligible: false,
                reason: errorReason,
                hasError: true,
                hasConnectionError
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedEmployer) {
            alert('Debe seleccionar un empleador');
            return;
        }

        if (!credits || credits < 1 || credits > 100) {
            alert('Los créditos deben estar entre 1 y 100');
            return;
        }

        if (!eligibility?.eligible) {
            alert(`Este empleador no puede recibir un código en este momento.\n\nRazón: ${eligibility?.reason || 'No especificada'}`);
            return;
        }

        setIsGenerating(true);
        try {
            const result = await finalGenerateCreditCode(selectedEmployer._id, credits, {
                description: description || undefined,
                expiresAt: expiresAt || undefined
            });

            if (result) {
                // Mostrar resultado y redirigir después de un tiempo
                alert(`Código generado: ${result.code}\nEnlace copiado al portapapeles`);
                setTimeout(() => {
                    NavigationService.navigateTo('/talentos/credit-codes');
                }, 2000);
            }
        } catch (error) {
            console.error('Error generating code:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const selectedEmployerData = employers.find(emp => emp._id === selectedEmployer);

    const handleBack = () => {
        // #region agent log
        // fetch('https://talentos-services.xn--am-yja.org/api/ingest/ac2cf331-943d-469d-9058-ce1350d849df',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'credit-codes/generate.js:handleBack',message:'handleBack called',data:{hasHistory:typeof window.history!=='undefined'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        NavigationService.navigateBack();
        // #region agent log
        // fetch('https://talentos-services.xn--am-yja.org/api/ingest/ac2cf331-943d-469d-9058-ce1350d849df',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'credit-codes/generate.js:handleBack',message:'NavigationService.navigateBack called',data:{hasHistory:!!NavigationService.history},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
    };

    return (
        <ToolbarComponent label="Generar Código de Crédito" showBackButton={true} onBackClick={handleBack}>
            <Row padding="0 20px">
                <br />
                <Col md={12}>
                    <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Empleador *</label>
                    <div className="employer-search-container">
                        <div className="search-input-wrapper">
                            <input
                                type="text"
                                value={employerSearch}
                                onChange={(e) => handleEmployerSearch(e.target.value)}
                                placeholder="Buscar por nombre, email o empresa..."
                                className="form-control employer-search-input"
                                required={!selectedEmployer}
                            />
                                        {selectedEmployer && (
                                            <button
                                                type="button"
                                                onClick={clearEmployerSelection}
                                                className="clear-employer-btn"
                                                title="Limpiar selección"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>

                                    {showEmployerDropdown && filteredEmployers.length > 0 && (
                                        <div className="employer-dropdown">
                                            {filteredEmployers.map(employer => (
                                                <div
                                                    key={employer._id}
                                                    className="employer-option"
                                                    onClick={() => selectEmployer(employer)}
                                                >
                                                    <div className="employer-name">{employer.name}</div>
                                                    <div className="employer-email">{employer.email}</div>
                                                    {employer.company && (
                                                        <div className="employer-company">{employer.company}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {employerSearch && filteredEmployers.length === 0 && showEmployerDropdown && (
                                        <div className="employer-dropdown">
                                            <div className="no-results">No se encontraron empleadores</div>
                                        </div>
                                    )}
                    </div>
                </Col>

                {selectedEmployer && (
                    <Col md={12}>
                            {eligibility && (
                                <div className="eligibility-info">
                                    {eligibility?.eligible ? (
                                        <div className="eligibility-eligible">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }}>
                                                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            Este empleador puede recibir un código de crédito
                                            {eligibility?.reason && (
                                                <div className="eligibility-detail">
                                                    {eligibility.reason}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="eligibility-not-eligible">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }}>
                                                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {eligibility?.reason || 'Este empleador no puede recibir un código en este momento'}
                                            {eligibility?.daysLeft && (
                                                <div className="eligibility-detail">
                                                    Disponible en {eligibility.daysLeft} días
                                                </div>
                                            )}
                                            {eligibility?.hasError && (
                                                <div className="eligibility-error-note">
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }}>
                                                        <path d="M8 2.66667C5.05448 2.66667 2.66667 5.05448 2.66667 8C2.66667 10.9455 5.05448 13.3333 8 13.3333C10.9455 13.3333 13.3333 10.9455 13.3333 8C13.3333 5.05448 10.9455 2.66667 8 2.66667ZM8 10.6667C7.63181 10.6667 7.33333 10.3682 7.33333 10C7.33333 9.63181 7.63181 9.33333 8 9.33333C8.36819 9.33333 8.66667 9.63181 8.66667 10C8.66667 10.3682 8.36819 10.6667 8 10.6667ZM8.66667 7.33333H7.33333V5.33333H8.66667V7.33333Z" fill="currentColor"/>
                                                    </svg>
                                                    Error de conexión - Contacte al administrador del sistema
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                    </Col>
                )}

                <Col md={6}>
                    <Input
                                label="Créditos *"
                                type="number"
                                min="1"
                                max="100"
                                value={credits}
                                onChange={(value) => setCredits(Number(value))}
                        placeholder="Cantidad de créditos (máx. 100)"
                        required
                    />
                </Col>

                <Col md={6}>
                    <Input
                                    label="Fecha de expiración"
                                    type="date"
                                    value={expiresAt}
                        onChange={setExpiresAt}
                        placeholder="Opcional"
                    />
                </Col>

                <Col md={12}>
                    <Input
                        label="Descripción"
                        type="textarea"
                        value={description}
                        onChange={setDescription}
                        placeholder="Descripción opcional del código"
                        rows={3}
                    />
                </Col>

                <Col md={12} className="text-right">
                    <br />
                    <PrimaryButton
                        text={isGenerating ? "Generando..." : "Generar Código"}
                        onClick={handleSubmit}
                        disabled={isGenerating || !eligibility?.eligible || !selectedEmployer}
                    />
                </Col>
            </Row>
        </ToolbarComponent>
    );
};

export default GenerateCreditCodePage;
